// Live's "Prompt Shortcuts" (#242, `editor-depth/SPEC.md` B6): the paperclip's list, each a
// prompt it puts in the chat box, in live's groups and order (live's `prompt-shortcuts-data.ts`,
// read for behaviour). Live's words; the two previews it plays from its own bucket are not here
// (spec docs/specs/editor/assistant.md decision 2).

export interface PromptShortcut {
  id: string;
  title: string;
  description: string;
  prompt: string;
  group: "EDITING" | "EFFECTS" | "SETTINGS";
}

export const PROMPT_SHORTCUTS: readonly PromptShortcut[] = [
  {
    id: "captions",
    title: "Auto Captions",
    description: "Automatically transcribe spoken audio and add animated captions.",
    prompt: "Automatically transcribe all spoken audio and add animated caption clips to the video.",
    group: "EDITING",
  },
  {
    id: "chroma",
    title: "Chroma Key",
    description: "Apply a green screen / chroma key effect to remove backgrounds.",
    prompt: "Apply a chroma key effect to remove the green screen background from my video clips.",
    group: "EFFECTS",
  },
  {
    id: "text",
    title: "Animated Text",
    description: "Add an animated text preset to the project.",
    prompt: "Add a stylish animated text preset to the beginning of the video.",
    group: "EDITING",
  },
  {
    id: "transitions",
    title: "Smooth Transitions",
    description: "Add transitions between all clips on the timeline.",
    prompt: "Add smooth crossfade transitions between all video clips on the timeline.",
    group: "EDITING",
  },
  {
    id: "effects",
    title: "Video Effects",
    description: "Apply a visual effect like VHS or glitch to the video.",
    prompt: "Add a vintage VHS glitch effect to the main video track.",
    group: "EFFECTS",
  },
  {
    id: "rough-cut",
    title: "Rough Cut",
    description: "Automatically create a rough cut from the uploaded media.",
    prompt: "Create a rough cut from all the uploaded media clips, removing silences.",
    group: "EDITING",
  },
  {
    id: "portrait",
    title: "Change to Portrait",
    description: "Adjust project settings for TikTok / Reels format (9:16).",
    prompt: "Change the project dimensions to 1080x1920 (portrait) for TikTok.",
    group: "SETTINGS",
  },
];

/**
 * The shortcuts a search finds (any part of the title, the description or the prompt, ignoring
 * case; all of them for a blank search), grouped in the order a group first appears, as live's.
 */
export function shortcutGroups(search: string): { group: string; shortcuts: PromptShortcut[] }[] {
  const wanted = search.trim().toLowerCase();
  const found = PROMPT_SHORTCUTS.filter(
    (shortcut) =>
      wanted === "" ||
      [shortcut.title, shortcut.description, shortcut.prompt].some((text) => text.toLowerCase().includes(wanted)),
  );
  const groups = new Map<string, PromptShortcut[]>();
  for (const shortcut of found) groups.set(shortcut.group, [...(groups.get(shortcut.group) ?? []), shortcut]);
  return [...groups].map(([group, shortcuts]) => ({ group, shortcuts }));
}
